import { ThemeEvents } from '@theme/events';

const UTM_STORAGE_KEY = 'aqt:utm-selling-plan-context';
const BEST_PLAN_STORAGE_KEY = 'aqt:best-selling-plan-record';
// Canonical attribution store written site-wide by assets/attribution.js.
const ATTRIBUTION_STORAGE_KEY = 'attribution_data';
// UTM keys the channel resolver understands and can pull from attribution.
const CHANNEL_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'affiliate'];

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * @param {string | number | null | undefined} value
 * @returns {string}
 */
function normalizePlanId(value) {
  return String(value ?? '').trim();
}

/**
 * @param {PlanAllocation} allocation
 * @param {string | number | null | undefined} sellingPlanId
 * @returns {boolean}
 */
function allocationMatchesSellingPlanId(allocation, sellingPlanId) {
  const normalizedId = normalizePlanId(sellingPlanId);
  return (
    normalizePlanId(allocation.sellingPlanId) === normalizedId ||
    normalizePlanId(allocation.sellingPlanIdNumeric) === normalizedId
  );
}

/**
 * @param {string} name
 * @returns {boolean}
 */
function isInstallmentPlan(name) {
  const n = normalizeKey(name);
  return /\b(klarna|affirm|after\s*pay|shop\s*pay\s*install|installment|split\s*pay|4\s*interest-free|4\s*payments|sezzle|quadpay|zip\s*pay)\b/.test(
    n
  );
}

/**
 * @typedef {Object} PlanAllocation
 * @property {string | number} sellingPlanId
 * @property {string} sellingPlanIdNumeric
 * @property {string} sellingPlanName
 * @property {string} deliveryFrequency
 * @property {string | null} description
 * @property {boolean} [isDefault]
 * @property {number} [position]
 * @property {string} groupId
 * @property {string} groupName
 * @property {number} price
 * @property {number | null} compareAtPrice
 */

/**
 * @typedef {Object} ChannelMapping
 * @property {string} channel_identifier
 * @property {string} selling_plan_group_name
 * @property {boolean} [enabled]
 * @property {number} [priority]
 */

/**
 * @typedef {Object} ChannelConfig
 * @property {number} [utmPersistenceTtlDays]
 * @property {ChannelMapping[]} mappings
 */

/**
 * @typedef {Object} BundleVariantMatch
 * @property {string | number} id
 * @property {number} price
 * @property {number | null} compareAtPrice
 */

/**
 * @typedef {Object} VariantPlansPayload
 * @property {Record<string, PlanAllocation[]>} variants
 * @property {Record<string, number>} [basePrices]
 * @property {Record<string, number | null>} [baseCompareAtPrices]
 * @property {Record<string, string[]>} [groupPlanOrder]
 * @property {{ id: string; name: string }[]} groups
 * @property {Record<string, BundleVariantMatch>} [bundleVariants]
 */

/**
 * @typedef {Object} BestPlanRecord
 * @property {string} sellingPlanId
 * @property {string} sellingPlanIdNumeric
 * @property {string} groupNameNormalized
 * @property {number} price
 * @property {number} updatedAt
 */

/**
 * @param {PlanAllocation[]} allocations
 * @returns {PlanAllocation[]}
 */
function filterRecurringAllocations(allocations) {
  return allocations.filter((a) => !isInstallmentPlan(a.sellingPlanName));
}

/**
 * @param {PlanAllocation[]} allocations
 * @returns {PlanAllocation | undefined}
 */
function pickCheapestAllocation(allocations) {
  const usable = filterRecurringAllocations(allocations);
  if (!usable.length) return undefined;
  return usable.reduce((best, cur) => (Number(cur.price) < Number(best.price) ? cur : best));
}

/**
 * @param {string} raw
 * @returns {ChannelConfig}
 */
function parseConfig(raw) {
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return { mappings: [], utmPersistenceTtlDays: 30 };
    }
    return {
      utmPersistenceTtlDays: Number(parsed.utmPersistenceTtlDays) || 30,
      mappings: Array.isArray(parsed.mappings) ? parsed.mappings : [],
    };
  } catch {
    return { mappings: [], utmPersistenceTtlDays: 30 };
  }
}

class ChannelSellingPlanRoot extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {string | null} Stored original variant ID when bundle swaps the form input */
  #savedOriginalVariantId = null;

  /** @type {ChannelConfig} */
  #config = { mappings: [], utmPersistenceTtlDays: 30 };

  /** @type {VariantPlansPayload | null} */
  #payload = null;

  /** @type {(event: Event) => void} */
  #onVariantUpdateBound = this.#onVariantUpdate.bind(this);

  /** @type {(event: Event) => void} */
  #onPurchaseTypeChangeBound = this.#onPurchaseTypeChange.bind(this);

  /** @type {(event: Event) => void} */
  #onSelectChangeBound = this.#onSelectChange.bind(this);

  /**
   * Enforces the Loop subscription price on the main/sticky price while a
   * subscription is selected. Defends against late writers (IOM's one-time
   * discount overlay and product-price.js re-rendering the base price).
   * @type {MutationObserver | null}
   */
  #iomPriceObserver = null;

  /**
   * The Loop subscription price (subunits) to keep displayed while subscription
   * is selected, or null when one-time is selected (no enforcement).
   * @type {number | null}
   */
  #activeSubscriptionPriceSubunits = null;

  /** @type {number | null} */
  #activeCompareAtSubunits = null;

  /**
   * Polling timer used to wait for IOM to finish initializing so the one-time
   * card can advertise the discounted price on load (IOM only applies the
   * offer to the live price once the one-time context is active).
   * @type {number | null}
   */
  #iomReadyTimer = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#section = this.closest('.shopify-section, dialog');
    this.#form = this.closest('form');
    this.#payload = this.#readPayload();

    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateBound, {
      signal: this.#abortController.signal,
    });

    const purchaseInputs = this.querySelectorAll('[data-channel-purchase-type]');
    for (const input of purchaseInputs) {
      input.addEventListener('change', this.#onPurchaseTypeChangeBound, {
        signal: this.#abortController.signal,
      });
    }

    this.querySelector('[data-channel-selling-plan-select]')?.addEventListener(
      'change',
      this.#onSelectChangeBound,
      { signal: this.#abortController.signal }
    );

    void this.#init();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#stopSubscriptionPriceEnforcement();
    if (this.#iomReadyTimer != null) {
      window.clearTimeout(this.#iomReadyTimer);
      this.#iomReadyTimer = null;
    }
    this.#section = null;
    this.#form = null;
    this.#payload = null;
    this.#savedOriginalVariantId = null;
  }

  /**
   * @returns {VariantPlansPayload | null}
   */
  #readPayload() {
    const el = this.querySelector('script[data-channel-selling-plan-variants]');
    if (!el?.textContent) return null;
    try {
      return /** @type {VariantPlansPayload} */ (JSON.parse(el.textContent));
    } catch {
      return null;
    }
  }

  async #init() {
    await this.#loadConfig();
    this.#persistUtmFromUrl();
    await this.#refreshPlanState();
    this.#scheduleIomDiscountRefresh();
  }

  /**
   * IOM initializes asynchronously (after the active-channels request resolves),
   * so on first paint its offers aren't available yet and the one-time card
   * shows the undiscounted base price. Poll briefly for IOM's resolved offers
   * and re-render the card once a matching discount is available.
   */
  #scheduleIomDiscountRefresh() {
    if (this.#iomReadyTimer != null) return;
    let attempts = 0;
    const tick = () => {
      this.#iomReadyTimer = null;
      attempts += 1;
      if (this.#getIomActiveOffers().length > 0) {
        void this.#refreshPlanState();
        return;
      }
      if (attempts >= 30) return; // ~6s budget
      this.#iomReadyTimer = window.setTimeout(tick, 200);
    };
    this.#iomReadyTimer = window.setTimeout(tick, 200);
  }

  async #loadConfig() {
    const url = this.dataset.configUrl;
    if (!url) return;
    try {
      const response = await fetch(url, { signal: this.#abortController?.signal });
      if (!response.ok) return;
      const text = await response.text();
      this.#config = parseConfig(text);
    } catch {
      this.#config = { mappings: [], utmPersistenceTtlDays: 30 };
    }
  }

  #ttlMs() {
    const days = this.#config.utmPersistenceTtlDays ?? 30;
    return Math.max(1, days) * 86400000;
  }

  /**
   * @returns {Record<string, string>}
   */
  #readUtmFromSearchParams() {
    const search = new URLSearchParams(window.location.search);
    /** @type {Record<string, string>} */
    const out = {};
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'affiliate', 'sub_channel'];
    for (const key of keys) {
      const v = search.get(key);
      if (v) out[key] = v;
    }
    return out;
  }

  /**
   * Reads the canonical attribution context captured site-wide by
   * attribution.js. This lets the selector honor UTMs captured on an earlier
   * page (landing page / homepage) even when the current PDP URL has no UTM
   * params, and picks up Everflow oid2/affid2 normalized to utm_source=everflow
   * + utm_medium=influencers.
   * @returns {Record<string, string>}
   */
  #readAttributionContext() {
    /** @type {Record<string, unknown>} */
    let data = {};

    try {
      const win = /** @type {any} */ (window);
      const fromApi = win.Attribution?.getAttribution?.();
      if (fromApi && typeof fromApi === 'object' && !Array.isArray(fromApi)) {
        data = fromApi;
      }
    } catch {
      data = {};
    }

    if (!Object.keys(data).length) {
      try {
        const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          data = parsed;
        }
      } catch {
        data = {};
      }
    }

    /** @type {Record<string, string>} */
    const out = {};
    for (const key of CHANNEL_UTM_KEYS) {
      const value = data[key];
      if (typeof value === 'string' && value) out[key] = value;
    }
    return out;
  }

  #persistUtmFromUrl() {
    const fromUrl = this.#readUtmFromSearchParams();
    const fromAttribution = this.#readAttributionContext();
    const prev = this.#loadStoredUtm();
    // Precedence: explicit current-URL UTMs win, then site-wide attribution,
    // then anything previously stored by this selector.
    const merged = { ...prev, ...fromAttribution, ...fromUrl, ts: Date.now() };
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged));
    } catch {
      /* ignore quota / private mode */
    }
  }

  /**
   * @returns {Record<string, string | number>}
   */
  #loadStoredUtm() {
    try {
      const raw = localStorage.getItem(UTM_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      const ts = Number(parsed.ts) || 0;
      if (Date.now() - ts > this.#ttlMs()) {
        localStorage.removeItem(UTM_STORAGE_KEY);
        return {};
      }
      return /** @type {Record<string, string | number>} */ (parsed);
    } catch {
      return {};
    }
  }

  /**
   * @param {Record<string, unknown>} source
   * @returns {string[]}
   */
  #channelKeysFromSource(source) {
    /** @type {string[]} */
    const keys = [];
    const pushNorm = (v) => {
      const n = normalizeKey(v);
      if (!n || keys.includes(n)) return;
      keys.push(n);
    };

    const orderedParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'affiliate', 'sub_channel'];
    for (const p of orderedParams) {
      const value = source[p];
      if (typeof value === 'string' && value) {
        pushNorm(value);
      }
    }

    const src = normalizeKey(source.utm_source);
    const med = normalizeKey(source.utm_medium);
    // Affiliate traffic reports utm_medium=influencers as of Aug 2026; 'affiliate'
    // is still accepted so visitors carrying attribution stored under the old
    // value keep resolving to the affiliate plan group until their TTL expires.
    if (src === 'everflow' && (med === 'influencers' || med === 'affiliate')) {
      pushNorm('affiliate');
    }

    return keys;
  }

  /**
   * @returns {string[]}
   */
  #getUrlChannelKeys() {
    const fromUrl = this.#readUtmFromSearchParams();
    return this.#channelKeysFromSource(fromUrl);
  }

  /**
   * @returns {string[]}
   */
  #getStoredChannelKeys() {
    const stored = this.#loadStoredUtm();
    return this.#channelKeysFromSource(stored);
  }

  /**
   * @returns {BestPlanRecord | null}
   */
  #loadBestPlan() {
    try {
      const raw = localStorage.getItem(BEST_PLAN_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return /** @type {BestPlanRecord} */ (parsed);
    } catch {
      return null;
    }
  }

  /**
   * @param {BestPlanRecord} record
   */
  #maybeSaveBestPlan(record) {
    const prev = this.#loadBestPlan();
    if (!prev || Number(record.price) <= Number(prev.price)) {
      try {
        localStorage.setItem(BEST_PLAN_STORAGE_KEY, JSON.stringify(record));
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * @returns {string}
   */
  #currentVariantId() {
    const fromForm = this.#form?.querySelector('input[name="id"]')?.value;
    return fromForm ? String(fromForm) : '';
  }

  /**
   * @param {string} variantId
   * @returns {PlanAllocation[]}
   */
  #allocationsForVariant(variantId) {
    if (!this.#payload?.variants || !variantId) return [];
    const list = this.#payload.variants[variantId];
    return Array.isArray(list) ? list : [];
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #sellingPlanInput() {
    return this.querySelector('[data-channel-selling-plan-input]');
  }

  /**
   * @returns {HTMLSelectElement | null}
   */
  #planSelect() {
    return this.querySelector('[data-channel-selling-plan-select]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #frequencyWrap() {
    return this.querySelector('[data-channel-frequency-wrap]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #oneTimePriceElement() {
    return this.querySelector('[data-channel-one-time-price]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionPriceElement() {
    return this.querySelector('[data-channel-subscription-price]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionTitleElement() {
    return this.querySelector('[data-channel-subscription-title]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionSubtitleElement() {
    return this.querySelector('[data-channel-subscription-subtitle]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionCompareElement() {
    return this.querySelector('[data-channel-subscription-compare]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionContentElement() {
    return this.querySelector('[data-channel-subscription-content]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #oneTimeCompareElement() {
    return this.querySelector('[data-channel-one-time-compare]');
  }

  /** @returns {HTMLElement | null} */
  #bundlePriceElement() {
    return this.querySelector('[data-channel-bundle-price]');
  }

  /** @returns {HTMLElement | null} */
  #bundleCompareElement() {
    return this.querySelector('[data-channel-bundle-compare]');
  }

  /**
   * @returns {string}
   */
  #currencyCode() {
    return this.dataset.currencyCode || 'USD';
  }

  /**
   * @param {number | string | null | undefined} subunits
   * @returns {string}
   */
  #formatMoney(subunits) {
    const amount = Number(subunits);
    if (!Number.isFinite(amount)) return '';
    const major = amount / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.#currencyCode(),
      }).format(major);
    } catch {
      return `$${major.toFixed(2)}`;
    }
  }

  /**
   * @param {string} variantId
   * @returns {number | null}
   */
  #basePriceForVariant(variantId) {
    const price = this.#payload?.basePrices?.[variantId];
    return Number.isFinite(Number(price)) ? Number(price) : null;
  }

  /**
   * @param {string} variantId
   * @returns {number | null}
   */
  #baseCompareAtForVariant(variantId) {
    const value = this.#payload?.baseCompareAtPrices?.[variantId];
    return Number.isFinite(Number(value)) ? Number(value) : null;
  }

  /**
   * @param {PlanAllocation[]} allocations
   * @returns {PlanAllocation[]}
   */
  #sortAllocationsByGroupOrder(allocations) {
    if (!allocations.length) return [];
    const groupId = allocations[0].groupId;
    const order = this.#payload?.groupPlanOrder?.[groupId];
    if (!Array.isArray(order) || !order.length) {
      return [...allocations];
    }

    const rank = new Map(order.map((id, idx) => [String(id), idx]));
    return [...allocations].sort((a, b) => {
      const aIdx = rank.has(normalizePlanId(a.sellingPlanId))
        ? rank.get(normalizePlanId(a.sellingPlanId))
        : rank.get(normalizePlanId(a.sellingPlanIdNumeric));
      const bIdx = rank.has(normalizePlanId(b.sellingPlanId))
        ? rank.get(normalizePlanId(b.sellingPlanId))
        : rank.get(normalizePlanId(b.sellingPlanIdNumeric));
      const ra = aIdx ?? Number.MAX_SAFE_INTEGER;
      const rb = bIdx ?? Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      return Number(a.price) - Number(b.price);
    });
  }

  /**
   * @param {string | null | undefined} html
   * @returns {{ heading: string; subtitle: string; rest: string }}
   */
  #parseDescriptionParts(html) {
    const empty = { heading: '', subtitle: '', rest: '' };
    const source = String(html ?? '').trim();
    if (!source) return empty;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${source}</div>`, 'text/html');
      const root = doc.body.firstElementChild;
      if (!root) return empty;

      const h2 = root.querySelector('h2');
      const heading = h2?.textContent?.trim() ?? '';
      if (h2) h2.remove();

      let subtitle = '';
      const firstBlock = root.firstElementChild;
      if (firstBlock && /^(DIV|P|SPAN|STRONG)$/i.test(firstBlock.tagName)) {
        subtitle = firstBlock.textContent?.trim() ?? '';
        firstBlock.remove();
      }

      return {
        heading,
        subtitle,
        rest: root.innerHTML.trim(),
      };
    } catch {
      return empty;
    }
  }

  /**
   * @param {'one-time' | 'subscription' | 'bundle'} purchaseType
   */
  #updateCardSelectionClasses(purchaseType) {
    const subCard = this.querySelector('[data-channel-subscription-card]');
    const oneTimeCard = this.querySelector('[data-channel-one-time-card]');
    const bundleCard = this.querySelector('[data-channel-bundle-card]');
    subCard?.classList.toggle('is-selected', purchaseType === 'subscription');
    oneTimeCard?.classList.toggle('is-selected', purchaseType === 'one-time');
    bundleCard?.classList.toggle('is-selected', purchaseType === 'bundle');
  }

  /**
   * @returns {HTMLElement[]}
   */
  #productPriceElements() {
    const selector = `product-price[data-product-id="${this.dataset.productId ?? ''}"]`;
    const scoped = this.#section ? Array.from(this.#section.querySelectorAll(selector)) : [];
    return /** @type {HTMLElement[]} */ (scoped.length ? scoped : Array.from(document.querySelectorAll(selector)));
  }

  /**
   * Sticky add-to-cart price containers (PDP only).
   * @returns {HTMLElement[]}
   */
  #stickyPriceElements() {
    const selector = `sticky-add-to-cart[data-product-id="${this.dataset.productId ?? ''}"] .sticky-add-to-cart__price`;
    const scoped = this.#section ? Array.from(this.#section.querySelectorAll(selector)) : [];
    return /** @type {HTMLElement[]} */ (scoped.length ? scoped : Array.from(document.querySelectorAll(selector)));
  }

  /**
   * @param {string} price
   * @returns {HTMLDivElement}
   */
  #buildProductPriceContainer(price) {
    const container = document.createElement('div');
    container.setAttribute('ref', 'priceContainer');

    const priceGroup = document.createElement('span');
    priceGroup.setAttribute('role', 'group');
    priceGroup.innerHTML =
      '<span class="visually-hidden">Sale price&nbsp;</span>' +
      `<span class="price" data-product-type="price">${price}</span>`;
    container.appendChild(priceGroup);

    return container;
  }

  /**
   * Reads IOM's channel-resolved active offers. IOM exposes them synchronously
   * once initialized via the public API, with the engine state as a fallback.
   * @returns {any[]}
   */
  #getIomActiveOffers() {
    const w = /** @type {any} */ (window);
    try {
      if (w.IOM && typeof w.IOM.getActiveOffers === 'function') {
        const offers = w.IOM.getActiveOffers();
        if (Array.isArray(offers)) return offers;
      }
    } catch (_error) {
      // no-op: fall back to the engine state below
    }
    const stateOffers = w.__IOM_STATE__ && w.__IOM_STATE__.activeOffers;
    return Array.isArray(stateOffers) ? stateOffers : [];
  }

  /**
   * Computes the IOM one-time discount for a variant from the active offers so
   * the one-time card can advertise the discounted price even while a
   * subscription is selected. IOM only applies the offer to the live price once
   * the one-time context is active, so we mirror its "highest" overlap handling
   * here for display. Returns null when no offer applies.
   * @param {string} variantId
   * @param {number | null} baseSubunits
   * @returns {{ discounted: number, savings: number } | null}
   */
  #resolveIomOneTimeDiscount(variantId, baseSubunits) {
    if (baseSubunits == null || !Number.isFinite(Number(baseSubunits))) return null;
    const offers = this.#getIomActiveOffers();
    if (!offers.length) return null;

    const variantGid = `gid://shopify/ProductVariant/${variantId}`;
    const productGid = `gid://shopify/Product/${String(this.dataset.productId || '')}`;

    let bestSavings = 0;
    for (const offer of offers) {
      const discounts = Array.isArray(offer?.discounts) ? offer.discounts : [];
      for (const discount of discounts) {
        const variantIds = Array.isArray(discount?.variant_ids) ? discount.variant_ids : [];
        const productIds = Array.isArray(discount?.product_ids) ? discount.product_ids : [];
        // variant_ids is the precise scope when present; otherwise fall back to
        // the product scope. Collection-only discounts are skipped (we can't
        // verify membership client-side).
        const matches = variantIds.length
          ? variantIds.includes(variantGid)
          : productIds.includes(productGid);
        if (!matches) continue;

        let savings = 0;
        if (discount.value_type === 'fixed_amount') {
          savings = Math.round(Number(discount.value) * 100);
        } else if (discount.value_type === 'percentage') {
          savings = Math.round((Number(baseSubunits) * Number(discount.value)) / 100);
        }
        if (Number.isFinite(savings) && savings > bestSavings) bestSavings = savings;
      }
    }

    if (bestSavings <= 0) return null;
    return { discounted: Math.max(0, Number(baseSubunits) - bestSavings), savings: bestSavings };
  }

  /**
   * Resolves IOM discount for the bundle product/variant.
   * Same logic as one-time but uses the bundle product's GID pair.
   * @returns {{ discounted: number, savings: number } | null}
   */
  /**
   * @returns {number | null}
   */
  #bundleVariantCompareAtPrice() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-channel-purchase-type="bundle"]'));
    const raw = input?.dataset?.bundleCompareAtPrice;
    const num = Number(raw);
    return (raw && Number.isFinite(num)) ? num : null;
  }

  #resolveIomBundleDiscount() {
    const bundleVariantId = this.#bundleVariantId();
    const bundleProductId = this.dataset.bundleProductId ?? '';
    const bundlePrice = this.#bundleVariantPrice();
    if (!bundleVariantId || !bundleProductId || bundlePrice == null) return null;

    const bundleCompareAt = this.#bundleVariantCompareAtPrice();
    const iomBase = (bundleCompareAt !== null && bundleCompareAt > bundlePrice) ? bundleCompareAt : bundlePrice;

    const offers = this.#getIomActiveOffers();
    if (!offers.length) return null;

    const variantGid = `gid://shopify/ProductVariant/${bundleVariantId}`;
    const productGid = `gid://shopify/Product/${bundleProductId}`;

    let bestSavings = 0;
    for (const offer of offers) {
      const discounts = Array.isArray(offer?.discounts) ? offer.discounts : [];
      for (const discount of discounts) {
        const variantIds = Array.isArray(discount?.variant_ids) ? discount.variant_ids : [];
        const productIds = Array.isArray(discount?.product_ids) ? discount.product_ids : [];
        const matches = variantIds.length
          ? variantIds.includes(variantGid)
          : productIds.includes(productGid);
        if (!matches) continue;

        let savings = 0;
        if (discount.value_type === 'fixed_amount') {
          savings = Math.round(Number(discount.value) * 100);
        } else if (discount.value_type === 'percentage') {
          savings = Math.round((Number(iomBase) * Number(discount.value)) / 100);
        }
        if (Number.isFinite(savings) && savings > bestSavings) bestSavings = savings;
      }
    }

    if (bestSavings <= 0) return null;
    return { discounted: Math.max(0, Number(iomBase) - bestSavings), savings: bestSavings };
  }

  /**
   * Applies IOM discount to the bundle card price and compare-at elements.
   * @param {HTMLElement | null} priceEl
   * @param {HTMLElement | null} compareEl
   */
  #updateBundleCardPricing(priceEl, compareEl) {
    const bundlePrice = this.#bundleVariantPrice();
    if (bundlePrice == null) return;

    const bundleCompareAt = this.#bundleVariantCompareAtPrice();
    const discount = this.#resolveIomBundleDiscount();
    if (priceEl) {
      priceEl.textContent = discount
        ? this.#formatMoney(discount.discounted)
        : this.#formatMoney(bundlePrice);
    }
    if (compareEl) {
      if (discount) {
        const strikePrice = (bundleCompareAt !== null && bundleCompareAt > bundlePrice) ? bundleCompareAt : bundlePrice;
        compareEl.textContent = this.#formatMoney(strikePrice);
      } else {
        const showCompare = bundleCompareAt !== null && bundleCompareAt > bundlePrice;
        compareEl.textContent = showCompare ? this.#formatMoney(bundleCompareAt) : '';
      }
    }
  }

  /**
   * @param {number | string | null | undefined} price
   * @param {number | null} [compareAt] — when provided, renders a
   *   strikethrough compare-at price and "Save" badge alongside the price.
   */
  #syncMainProductPrice(price, compareAt = null) {
    const formattedPrice = this.#formatMoney(price);
    if (!formattedPrice) return;

    const savings = (compareAt != null && price != null && Number(compareAt) > Number(price))
      ? Number(compareAt) - Number(price)
      : 0;
    const formattedCompare = savings > 0 ? this.#formatMoney(compareAt) : null;
    const formattedSavings = savings > 0 ? this.#formatMoney(savings) : null;

    const priceTargets = [...this.#productPriceElements(), ...this.#stickyPriceElements()];
    for (const target of priceTargets) {
      const priceContainer = target.querySelector('[ref="priceContainer"]');
      if (!(priceContainer instanceof HTMLElement)) continue;


      if (formattedCompare && formattedSavings) {
        priceContainer.innerHTML =
          `<s class="compare-at-price">${formattedCompare}</s> ` +
          `<span class="price" data-product-type="price">${formattedPrice}</span> ` +
          `<span class="bundle-savings-badge" style="background-color:#006400;color:#fff;padding:2px 6px;border-radius:3px;font-size:0.85em;">Save ${formattedSavings}</span>`;
      } else {
        priceContainer.querySelectorAll('.compare-at-price, .bundle-savings-badge').forEach((el) => el.remove());
        const priceEl = priceContainer.querySelector('.price, [data-product-type="price"]');
        if (priceEl instanceof HTMLElement) {
          priceEl.textContent = formattedPrice;
        } else {
          const built = this.#buildProductPriceContainer(formattedPrice);
          priceContainer.innerHTML = built.innerHTML;
        }
      }
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #priceSyncTargets() {
    return [...this.#productPriceElements(), ...this.#stickyPriceElements()];
  }

  /**
   * Removes IOM's injected one-time discount markup from the main/sticky price
   * containers. Mirrors the cleanup in sticky-add-to-cart.js.
   */
  #cleanIomOverlay() {
    for (const target of this.#priceSyncTargets()) {
      target
        .querySelectorAll('.iom-original-price, .iom-discount-amount, .iom-discount-price')
        .forEach((el) => el.remove());
      target
        .querySelectorAll('.iom-price-overlay')
        .forEach((el) => el.remove());
      target.querySelectorAll('.iom-original-content-hidden').forEach((wrapper) => {
        while (wrapper.firstChild) wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
        wrapper.remove();
      });
      for (const attr of Array.from(target.attributes)) {
        if (attr.name.startsWith('data-iom')) target.removeAttribute(attr.name);
      }
      const priceContainer = target.querySelector('[ref="priceContainer"]');
      if (priceContainer) {
        for (const attr of Array.from(priceContainer.attributes)) {
          if (attr.name.startsWith('data-iom')) priceContainer.removeAttribute(attr.name);
        }
        const priceEl = priceContainer.querySelector('.price, [data-product-type="price"]');
        if (priceEl) {
          for (const attr of Array.from(priceEl.attributes)) {
            if (attr.name.startsWith('data-iom')) priceEl.removeAttribute(attr.name);
          }
        }
      }
    }
  }

  /**
   * Strips IOM's injected overlay and processing markers from an element so we
   * can fully own its content. Used on the one-time card while a subscription is
   * selected, leaving the element clean for IOM to reprocess on a later
   * one-time selection.
   * @param {HTMLElement} el
   */
  #clearIomArtifacts(el) {
    el.querySelectorAll('.iom-original-content-hidden, .iom-price-overlay').forEach((node) => node.remove());
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith('data-iom')) el.removeAttribute(attr.name);
    }
  }

  /**
   * Re-asserts the Loop subscription price on the main/sticky price and strips
   * any IOM one-time overlay. Runs with the observer detached so our own writes
   * don't retrigger it. External writers (IOM, product-price.js) re-trigger it.
   */
  #enforceSubscriptionPrice() {
    if (this.#activeSubscriptionPriceSubunits == null) return;
    this.#iomPriceObserver?.disconnect();
    this.#cleanIomOverlay();
    this.#syncMainProductPrice(this.#activeSubscriptionPriceSubunits, this.#activeCompareAtSubunits);
    if (this.#iomPriceObserver) {
      for (const target of this.#priceSyncTargets()) {
        this.#iomPriceObserver.observe(target, { childList: true, subtree: true });
      }
    }
  }

  /**
   * @param {number} priceSubunits Price to keep displayed (subscription or bundle).
   * @param {number | null} [compareAtSubunits] Compare-at price for bundle display.
   */
  #startSubscriptionPriceEnforcement(priceSubunits, compareAtSubunits = null) {
    this.#activeSubscriptionPriceSubunits = priceSubunits;
    this.#activeCompareAtSubunits = compareAtSubunits;
    this.#enforceSubscriptionPrice();
    if (this.#iomPriceObserver) return;
    this.#iomPriceObserver = new MutationObserver(() => this.#enforceSubscriptionPrice());
    for (const target of this.#priceSyncTargets()) {
      this.#iomPriceObserver.observe(target, { childList: true, subtree: true });
    }
  }

  #stopSubscriptionPriceEnforcement() {
    this.#activeSubscriptionPriceSubunits = null;
    this.#activeCompareAtSubunits = null;
    this.#iomPriceObserver?.disconnect();
    this.#iomPriceObserver = null;
  }

  /**
   * @param {PlanAllocation | undefined} plan
   * @param {string} variantId
   * @param {'one-time' | 'subscription' | 'bundle'} purchaseType
   */
  #updateVisiblePricing(plan, variantId, purchaseType) {
    const oneTimeEl = this.#oneTimePriceElement();
    const subEl = this.#subscriptionPriceElement();
    const subTitleEl = this.#subscriptionTitleElement();
    const subSubtitleEl = this.#subscriptionSubtitleElement();
    const subCompareEl = this.#subscriptionCompareElement();
    const subContentEl = this.#subscriptionContentElement();
    const oneTimeCompareEl = this.#oneTimeCompareElement();
    const bundlePriceEl = this.#bundlePriceElement();
    const bundleCompareEl = this.#bundleCompareElement();

    const basePrice = this.#basePriceForVariant(variantId);
    const baseCompareAt = this.#baseCompareAtForVariant(variantId);

    let mainDisplayPrice = basePrice;
    let mainCompareAt = null;
    if (purchaseType === 'subscription' && plan) {
      mainDisplayPrice = plan.price;
    } else if (purchaseType === 'bundle') {
      const bp = this.#bundleVariantPrice();
      const bd = this.#resolveIomBundleDiscount();
      mainDisplayPrice = bd ? bd.discounted : bp;
      const bca = this.#bundleVariantCompareAtPrice();
      const strikePrice = (bca != null && bp != null && bca > bp) ? bca : bp;
      if (strikePrice != null && mainDisplayPrice != null && strikePrice > Number(mainDisplayPrice)) {
        mainCompareAt = strikePrice;
      }
    }
    this.#syncMainProductPrice(mainDisplayPrice, mainCompareAt);

    this.#updateBundleCardPricing(bundlePriceEl, bundleCompareEl);

    oneTimeEl?.setAttribute('data-variant-id', variantId);
    oneTimeEl?.setAttribute('data-product-id', String(this.dataset.productId || ''));

    if (purchaseType === 'one-time') {
      // One-time is the active context: IOM discounts the live price element
      // itself (it carries data-product-type="price"). Seed it with the base
      // price so IOM has the correct original to discount, and don't clobber
      // IOM's overlay once it has processed the element.
      const iomProcessed = !!(oneTimeEl && oneTimeEl.hasAttribute('data-iom-processed'));
      if (oneTimeEl && !iomProcessed) {
        oneTimeEl.textContent = basePrice !== null ? this.#formatMoney(basePrice) : '';
      }
      if (oneTimeCompareEl) {
        if (iomProcessed) {
          // IOM renders its own struck original price inside the price element.
          oneTimeCompareEl.textContent = '';
        } else {
          const showCompare = baseCompareAt !== null && basePrice !== null && baseCompareAt > basePrice;
          oneTimeCompareEl.textContent = showCompare ? this.#formatMoney(baseCompareAt) : '';
        }
      }
    } else {
      // Subscription/bundle is selected: seed the one-time card with the base
      // price. When IOM offers apply, IOM may reprocess the element and inject
      // its own overlay; in that case keep our compare-at empty so IOM's
      // overlay and our span don't stack.
      if (oneTimeEl) {
        this.#clearIomArtifacts(oneTimeEl);
        oneTimeEl.textContent = basePrice !== null ? this.#formatMoney(basePrice) : '';
      }
      if (oneTimeCompareEl) {
        const showCompare = baseCompareAt !== null && basePrice !== null && baseCompareAt > basePrice;
        oneTimeCompareEl.textContent = showCompare ? this.#formatMoney(baseCompareAt) : '';
      }
    }

    if (subEl) {
      subEl.textContent = plan ? this.#formatMoney(plan.price) : '';
    }

    const parsed = this.#parseDescriptionParts(plan?.description);
    if (subTitleEl) {
      const fallback = subTitleEl.dataset.defaultLabel || '';
      subTitleEl.textContent = parsed.heading || plan?.groupName || fallback;
    }
    if (subSubtitleEl) {
      const fallback = subSubtitleEl.dataset.defaultSubtitle || '';
      subSubtitleEl.textContent = parsed.subtitle || fallback;
    }
    if (subCompareEl) {
      const showCompare = !!(plan?.compareAtPrice && Number(plan.compareAtPrice) > Number(plan.price));
      subCompareEl.textContent = showCompare ? this.#formatMoney(plan?.compareAtPrice ?? null) : '';
    }
    if (subContentEl) {
      subContentEl.innerHTML = parsed.rest;
    }

    // The price below the selections is owned by us while subscription or
    // bundle is selected, so keep enforcing it against IOM's one-time overlay
    // and any product-price.js re-render. One-time releases enforcement so
    // IOM's discounted one-time price can render.
    if (purchaseType === 'subscription' && plan) {
      this.#startSubscriptionPriceEnforcement(Number(plan.price));
    } else if (purchaseType === 'bundle' && mainDisplayPrice != null) {
      this.#startSubscriptionPriceEnforcement(Number(mainDisplayPrice), mainCompareAt);
    } else {
      this.#stopSubscriptionPriceEnforcement();
    }
    this.#updateCardSelectionClasses(purchaseType);
  }

  /**
   * @returns {'one-time' | 'subscription' | 'bundle'}
   */
  #purchaseType() {
    if (this.dataset.requiresSellingPlan === 'true') {
      return 'subscription';
    }
    const checked = this.querySelector('[data-channel-purchase-type]:checked');
    const v = checked?.getAttribute('value');
    if (v === 'subscription') return 'subscription';
    if (v === 'bundle') return 'bundle';
    return 'one-time';
  }

  /**
   * @param {PlanAllocation[]} groupAllocs
   * @param {string} defaultPlanId
   */
  #selectDefaultPlanInGroup(groupAllocs, defaultPlanId) {
    const usable = filterRecurringAllocations(groupAllocs);
    if (!usable.length) return undefined;

    const explicitDefault = usable.find((a) => a.isDefault === true);
    if (explicitDefault) return explicitDefault;

    const normDefault = normalizeKey(defaultPlanId);
    if (normDefault) {
      const match = usable.find(
        (a) =>
          normalizeKey(a.sellingPlanId) === normDefault ||
          normalizeKey(a.sellingPlanIdNumeric) === normDefault ||
          a.sellingPlanId === defaultPlanId ||
          a.sellingPlanIdNumeric === defaultPlanId
      );
      if (match) return match;
    }
    const orderedByPosition = [...usable].sort((a, b) => {
      const pa = Number(a.position ?? 0);
      const pb = Number(b.position ?? 0);
      if (pa !== pb) return pa - pb;
      return 0;
    });
    if (orderedByPosition[0] && Number(orderedByPosition[0].position ?? 0) > 0) {
      return orderedByPosition[0];
    }

    const ordered = this.#sortAllocationsByGroupOrder(usable);
    return ordered[0] ?? pickCheapestAllocation(usable);
  }

  /**
   * @param {string} variantId
   * @returns {{ allocations: PlanAllocation[]; selected: PlanAllocation | undefined }}
   */
  #resolveSubscriptionSelection(variantId) {
    const all = this.#allocationsForVariant(variantId);
    const recurring = filterRecurringAllocations(all);
    const defaultPlanId = this.dataset.defaultSellingPlanId ?? '';

    if (!recurring.length) {
      return { allocations: [], selected: undefined };
    }

    const urlKeys = this.#getUrlChannelKeys();
    const storedKeys = this.#getStoredChannelKeys();
    const mappings = this.#config.mappings.filter((m) => m.enabled !== false);

    /**
     * @param {string[]} keys
     * @returns {{ allocations: PlanAllocation[]; selected: PlanAllocation | undefined } | undefined}
     */
    const resolveFromKeys = (keys) => {
      /** @type {Map<string, { priority: number; allocs: PlanAllocation[] }>} */
      const triggered = new Map();

      for (const key of keys) {
        for (const m of mappings) {
          if (normalizeKey(m.channel_identifier) === 'default') continue;
          if (normalizeKey(m.channel_identifier) !== key) continue;
          const gNorm = normalizeKey(m.selling_plan_group_name);
          const inGroup = recurring.filter((a) => normalizeKey(a.groupName) === gNorm);
          if (!inGroup.length) continue;
          const priority = Number(m.priority) || 0;
          const prev = triggered.get(gNorm);
          if (!prev) {
            triggered.set(gNorm, { priority, allocs: [...inGroup] });
          } else {
            prev.priority = Math.max(prev.priority, priority);
            const ids = new Set(prev.allocs.map((a) => a.sellingPlanId));
            for (const a of inGroup) {
              if (!ids.has(a.sellingPlanId)) prev.allocs.push(a);
            }
          }
        }
      }

      if (!triggered.size) return undefined;
      /** @type {{ gNorm: string; minPrice: number; priority: number; allocs: PlanAllocation[] }[]} */
      const rows = [];
      for (const [gNorm, { priority, allocs }] of triggered) {
        const cheapest = pickCheapestAllocation(allocs);
        if (!cheapest) continue;
        rows.push({
          gNorm,
          minPrice: Number(cheapest.price),
          priority,
          allocs,
        });
      }
      if (!rows.length) return undefined;
      rows.sort((a, b) => {
        if (a.minPrice !== b.minPrice) return a.minPrice - b.minPrice;
        return b.priority - a.priority;
      });
      const winner = rows[0];
      const selected =
        this.#selectDefaultPlanInGroup(winner.allocs, defaultPlanId) ?? pickCheapestAllocation(winner.allocs);
      return { allocations: winner.allocs, selected };
    };

    const stored = this.#loadBestPlan();
    /** @type {{ allocations: PlanAllocation[]; selected: PlanAllocation | undefined } | undefined} */
    let fromRetainedBest;
    if (stored?.sellingPlanId) {
      const byId = recurring.find(
        (a) =>
          allocationMatchesSellingPlanId(a, stored.sellingPlanId) ||
          allocationMatchesSellingPlanId(a, stored.sellingPlanIdNumeric)
      );
      if (byId) {
        const sameGroup = recurring.filter((a) => normalizeKey(a.groupName) === normalizeKey(byId.groupName));
        fromRetainedBest = { allocations: sameGroup, selected: byId };
      }
    }

    if (!fromRetainedBest?.selected && stored?.groupNameNormalized) {
      const inGroup = recurring.filter((a) => normalizeKey(a.groupName) === stored.groupNameNormalized);
      if (inGroup.length) {
        const selected = this.#selectDefaultPlanInGroup(inGroup, defaultPlanId) ?? pickCheapestAllocation(inGroup);
        fromRetainedBest = { allocations: inGroup, selected };
      }
    }

    // Resolve the channel from explicit current-URL UTMs first, then from the
    // stored attribution context (UTMs captured on an earlier page, e.g. the
    // homepage or a landing page, where this component isn't rendered).
    const fromUrlUtm = resolveFromKeys(urlKeys);
    const fromStoredUtm = resolveFromKeys(storedKeys);
    const fromChannel = fromUrlUtm?.selected ? fromUrlUtm : fromStoredUtm;

    // When a channel resolves and we also have a retained best, keep the lower priced plan.
    if (fromChannel?.selected && fromRetainedBest?.selected) {
      if (Number(fromChannel.selected.price) < Number(fromRetainedBest.selected.price)) {
        return fromChannel;
      }
      return fromRetainedBest;
    }
    if (fromChannel?.selected) {
      return fromChannel;
    }
    if (fromRetainedBest?.selected) {
      return fromRetainedBest;
    }

    const defaultMapping = mappings.find((m) => normalizeKey(m.channel_identifier) === 'default');
    if (defaultMapping) {
      const gNorm = normalizeKey(defaultMapping.selling_plan_group_name);
      const inGroup = recurring.filter((a) => normalizeKey(a.groupName) === gNorm);
      if (inGroup.length) {
        const selected =
          this.#selectDefaultPlanInGroup(inGroup, defaultPlanId) ?? pickCheapestAllocation(inGroup);
        return { allocations: inGroup, selected };
      }
    }

    const cheapestOverall = pickCheapestAllocation(recurring);
    if (cheapestOverall) {
      const sameGroup = recurring.filter(
        (a) => normalizeKey(a.groupName) === normalizeKey(cheapestOverall.groupName)
      );
      const selected =
        this.#selectDefaultPlanInGroup(sameGroup, defaultPlanId) ?? pickCheapestAllocation(sameGroup);
      return { allocations: sameGroup, selected };
    }

    return { allocations: [], selected: undefined };
  }

  /**
   * @param {PlanAllocation[]} allocations
   */
  #fillSelectOptions(allocations) {
    const select = this.#planSelect();
    if (!select) return;
    select.innerHTML = '';
    const usable = filterRecurringAllocations(allocations);
    const sorted = this.#sortAllocationsByGroupOrder(usable);
    for (const a of sorted) {
      const opt = document.createElement('option');
      opt.value = a.sellingPlanId;
      opt.textContent = a.deliveryFrequency || a.sellingPlanName || a.sellingPlanId;
      select.appendChild(opt);
    }
  }

  /**
   * @param {string | number} sellingPlanId
   */
  #syncSellingPlanInput(sellingPlanId) {
    const input = this.#sellingPlanInput();
    if (!input) return;
    input.value = sellingPlanId;
    input.disabled = !sellingPlanId;
  }

  /**
   * Reads the bundle variant ID from the checked bundle radio input.
   * @returns {string}
   */
  #bundleVariantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-channel-purchase-type="bundle"]'));
    return input?.dataset?.bundleVariantId ?? this.dataset.bundleVariantId ?? '';
  }

  /**
   * Looks up the bundle variant that matches a given main-product variant
   * (matched by color in the Liquid payload), if any.
   * @param {string} mainVariantId
   * @returns {BundleVariantMatch | null}
   */
  #bundleVariantForMain(mainVariantId) {
    const map = this.#payload?.bundleVariants;
    if (!map || !mainVariantId) return null;
    return map[mainVariantId] ?? null;
  }

  /**
   * Syncs the bundle radio's dataset (variant id + pricing) to the bundle
   * variant matching the currently selected main-product variant. Keeps the
   * bundle card price accurate and ensures add-to-cart swaps in the same-color
   * bundle variant rather than always the default one.
   * @param {string} mainVariantId
   */
  #syncBundleVariantForMain(mainVariantId) {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-channel-purchase-type="bundle"]'));
    if (!input) return;
    const matched = this.#bundleVariantForMain(mainVariantId);
    if (!matched || matched.id == null) return;
    input.dataset.bundleVariantId = String(matched.id);
    if (matched.price != null) {
      input.dataset.bundlePrice = String(matched.price);
    }
    if (matched.compareAtPrice != null) {
      input.dataset.bundleCompareAtPrice = String(matched.compareAtPrice);
    } else {
      input.dataset.bundleCompareAtPrice = '';
    }
  }

  /**
   * Reads the bundle variant price (in subunits) from the checked bundle radio input.
   * @returns {number | null}
   */
  #bundleVariantPrice() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-channel-purchase-type="bundle"]'));
    const raw = input?.dataset?.bundlePrice;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  }

  /**
   * Swaps the form's hidden variant ID input to a different variant.
   * Stores the original so it can be restored later.
   * @param {string} variantId
   */
  #swapFormVariantId(variantId) {
    const input = /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('input[name="id"]'));
    if (!input) return;
    if (!this.#savedOriginalVariantId) {
      this.#savedOriginalVariantId = input.value;
    }
    input.value = variantId;
  }

  /**
   * Restores the form's hidden variant ID to the original product variant.
   */
  #restoreFormVariantId() {
    if (!this.#savedOriginalVariantId) return;
    const input = /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('input[name="id"]'));
    if (input) {
      input.value = this.#savedOriginalVariantId;
    }
    this.#savedOriginalVariantId = null;
  }

  /**
   * Returns the main-product variant ID even when the form input has been
   * swapped to a bundle variant.
   * @returns {string}
   */
  #originalVariantId() {
    return this.#savedOriginalVariantId ?? this.#currentVariantId();
  }

  async #refreshPlanState() {
    const purchaseType = this.#purchaseType();
    const frequencyWrap = this.#frequencyWrap();
    const select = this.#planSelect();

    if (purchaseType === 'bundle') {
      const mainVariantId = this.#originalVariantId();
      this.#syncBundleVariantForMain(mainVariantId);
      const subscriptionPreview = this.#resolveSubscriptionSelection(mainVariantId).selected;
      frequencyWrap?.setAttribute('hidden', '');
      select && (select.innerHTML = '');
      this.#syncSellingPlanInput('');

      const bundleVid = this.#bundleVariantId();
      if (bundleVid) {
        this.#swapFormVariantId(bundleVid);
      }

      this.#updateVisiblePricing(subscriptionPreview, mainVariantId, 'bundle');
      return;
    }

    this.#restoreFormVariantId();
    const variantId = this.#currentVariantId();
    this.#syncBundleVariantForMain(variantId);

    if (purchaseType === 'one-time') {
      const subscriptionPreview = this.#resolveSubscriptionSelection(variantId).selected;
      frequencyWrap?.setAttribute('hidden', '');
      select && (select.innerHTML = '');
      this.#syncSellingPlanInput('');
      this.#updateVisiblePricing(subscriptionPreview, variantId, 'one-time');
      return;
    }

    frequencyWrap?.removeAttribute('hidden');

    const { allocations, selected } = this.#resolveSubscriptionSelection(variantId);
    this.#fillSelectOptions(allocations);

    let plan = selected;
    if (plan && select) {
      const matchOption = Array.from(select.options).some((o) => allocationMatchesSellingPlanId(plan, o.value));
      if (!matchOption) {
        plan = pickCheapestAllocation(allocations);
      }
    }

    if (select && plan) {
      select.value = plan.sellingPlanId;
    }

    if (plan) {
      this.#syncSellingPlanInput(plan.sellingPlanId);
      this.#maybeSaveBestPlan({
        sellingPlanId: plan.sellingPlanId,
        sellingPlanIdNumeric: plan.sellingPlanIdNumeric,
        groupNameNormalized: normalizeKey(plan.groupName),
        price: Number(plan.price),
        updatedAt: Date.now(),
      });
      this.#updateVisiblePricing(plan, variantId, 'subscription');
    } else {
      const oneTimeRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector('[data-channel-purchase-type="one-time"]')
      );
      if (oneTimeRadio) {
        oneTimeRadio.checked = true;
        frequencyWrap?.setAttribute('hidden', '');
        select && (select.innerHTML = '');
      }
      this.#syncSellingPlanInput('');
      this.#updateVisiblePricing(undefined, variantId, this.#purchaseType());
    }
  }

  /** @param {Event} event */
  #onVariantUpdate(event) {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const productId = detail?.data?.productId;
    if (productId && String(productId) !== String(this.dataset.productId ?? '')) {
      return;
    }
    // The product form's own variant:update handler resets input[name="id"] to
    // the new main variant. Drop any saved swap so #originalVariantId() reads
    // the new main variant; the bundle branch re-swaps to the matching variant.
    this.#savedOriginalVariantId = null;
    window.requestAnimationFrame(() => {
      void this.#refreshPlanState();
    });
  }

  #onPurchaseTypeChange() {
    void this.#refreshPlanState();
  }

  #onSelectChange() {
    const select = this.#planSelect();
    const id = select?.value ?? '';
    this.#syncSellingPlanInput(id);
    const variantId = this.#currentVariantId();
    const all = this.#allocationsForVariant(variantId);
    const plan = all.find((a) => allocationMatchesSellingPlanId(a, id));
    this.#updateVisiblePricing(plan, variantId, 'subscription');
    if (plan) {
      this.#maybeSaveBestPlan({
        sellingPlanId: plan.sellingPlanId,
        sellingPlanIdNumeric: plan.sellingPlanIdNumeric,
        groupNameNormalized: normalizeKey(plan.groupName),
        price: Number(plan.price),
        updatedAt: Date.now(),
      });
    }
  }
}

if (!customElements.get('channel-selling-plan-root')) {
  customElements.define('channel-selling-plan-root', ChannelSellingPlanRoot);
}
